// =============================================================================
// VandieZ admin-web -- WS-B pricing types (migration 0076)
// =============================================================================
// Mirrors app/schemas/fleet.py. The eight models are fixed by D-2; which are
// ENABLED is data, which is the whole point of AD-07 -- turning one on must
// not require an app release.
// =============================================================================

export type PricingModelCode =
  | "bidding"
  | "fixed"
  | "km"
  | "time"
  | "weight"
  | "volume"
  | "zone"
  | "custom";

export type TollHandling = "excluded" | "included" | "allowance";

export interface PricingModel {
  code: string;
  display_name: string;
  description: string;
  is_enabled: boolean;
  /**
   * p9 "Show recommended model". Exactly one model carries this -- a
   * partial unique index enforces it -- and the shipper's post form
   * preselects it.
   */
  is_recommended: boolean;
  sort_order: number;
}

export interface PricingModelListResponse {
  items: PricingModel[];
  total: number;
}

export interface FareRule {
  id: string;
  pricing_model: string;
  vehicle_type: string | null;
  pickup_district: string | null;
  drop_district: string | null;
  pickup_zone_id: string | null;
  drop_zone_id: string | null;
  base_fare_inr: string | null;
  minimum_fare_inr: string | null;
  flat_price_inr: string | null;
  per_km_rate_inr: string | null;
  per_hour_rate_inr: string | null;
  per_tonne_rate_inr: string | null;
  per_cbm_rate_inr: string | null;
  waiting_fee_per_hour_inr: string | null;
  free_waiting_minutes: number;
  toll_handling: string;
  toll_allowance_inr: string | null;
  effective_from: string;
  /** Non-null means SUPERSEDED -- kept for audit, no longer resolving. */
  effective_to: string | null;
  notes: string | null;
}

export interface FareRuleListResponse {
  items: FareRule[];
  total: number;
}

export interface FareRuleCreatePayload {
  pricing_model: PricingModelCode;
  vehicle_type?: string | null;
  pickup_district?: string | null;
  drop_district?: string | null;
  base_fare_inr?: string | null;
  minimum_fare_inr?: string | null;
  flat_price_inr?: string | null;
  per_km_rate_inr?: string | null;
  per_hour_rate_inr?: string | null;
  per_tonne_rate_inr?: string | null;
  per_cbm_rate_inr?: string | null;
  waiting_fee_per_hour_inr?: string | null;
  free_waiting_minutes?: number;
  toll_handling?: TollHandling;
  toll_allowance_inr?: string | null;
  notes?: string | null;
}

/**
 * Which component fields a given model actually needs.
 *
 * Mirrors the server-side validator in `FareRuleCreate._model_has_what_it_needs`.
 * Duplicated deliberately: the server is the authority and rejects an
 * incomplete rule, but an admin should find that out while typing rather
 * than after submitting a form they have to reconstruct.
 */
export const REQUIRED_FIELDS_BY_MODEL: Record<string, readonly string[]> = {
  fixed: ["flat_price_inr"],
  zone: ["flat_price_inr"],
  km: ["per_km_rate_inr"],
  time: ["per_hour_rate_inr"],
  weight: ["per_tonne_rate_inr"],
  volume: ["per_cbm_rate_inr"],
  custom: [],
  bidding: [],
};

export const FIELD_LABELS: Record<string, string> = {
  base_fare_inr: "Base fare (₹)",
  minimum_fare_inr: "Minimum fare (₹)",
  flat_price_inr: "Flat price (₹)",
  per_km_rate_inr: "Per km (₹)",
  per_hour_rate_inr: "Per hour (₹)",
  per_tonne_rate_inr: "Per tonne (₹)",
  per_cbm_rate_inr: "Per CBM (₹)",
  waiting_fee_per_hour_inr: "Waiting fee per hour (₹)",
  toll_allowance_inr: "Toll allowance (₹)",
};
